import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { dashboardAPI, categoryAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import {
  FileText, Clock, IndianRupee,
  Wallet, TrendingUp, ArrowRight, AlertTriangle
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [budgetAlerts, setBudgetAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(() => {
    Promise.all([
      dashboardAPI.stats(),
      categoryAPI.budgetStatus()
    ])
      .then(([statsRes, budgetRes]) => {
        setStats(statsRes.data.stats);
        const alerts = budgetRes.data.statuses.filter(s => s.alertLevel !== 'normal' && s.budget > 0);
        setBudgetAlerts(alerts);
      })
      .catch(() => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const pieColors = ['#0F172A', '#334155', '#64748B', '#94A3B8', '#CBD5E1'];
  const chartTick = useMemo(() => ({ fontSize: 10, fill: '#64748B' }), []);
  const tooltipStyle = useMemo(() => ({ fontSize: '12px', borderRadius: '6px', border: '1px solid #E2E8F0' }), []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const cards = [
    { label: 'Total Vouchers', value: stats?.totalVouchers || 0, icon: FileText, color: 'text-slate-900' },
    { label: 'Pending Approvals', value: stats?.pendingApprovals || 0, icon: Clock, color: 'text-amber-600', alert: (stats?.pendingApprovals || 0) > 0 },
    { label: 'Today\'s Outflow', value: `₹${(stats?.todayTotal || 0).toLocaleString('en-IN')}`, icon: IndianRupee, color: 'text-slate-900' },
    { label: 'Monthly Total', value: `₹${(stats?.monthlyTotal || 0).toLocaleString('en-IN')}`, icon: TrendingUp, color: 'text-slate-900' },
    { label: 'Outstanding Advances', value: `₹${(stats?.outstandingAdvances || 0).toLocaleString('en-IN')}`, icon: Wallet, color: 'text-blue-600' },
    { label: 'Today\'s Vouchers', value: stats?.todayVouchers || 0, icon: FileText, color: 'text-slate-900' },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-0.5">Welcome back, {user?.name}</p>
        </div>
        <Link
          to="/vouchers/new"
          className="bg-slate-900 text-white text-sm px-4 py-2 rounded-md hover:bg-slate-800 transition-colors inline-flex items-center gap-1.5"
          data-testid="quick-add-voucher-btn"
        >
          <FileText className="w-4 h-4" /> Add Voucher
        </Link>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {cards.map((card, i) => {
          const Icon = card.icon;
          return (
            <div
              key={card.label}
              className={`bg-white border rounded-lg p-3 ${card.alert ? 'border-amber-300 bg-amber-50/50' : 'border-slate-200'}`}
              data-testid={`stat-card-${card.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="flex items-center gap-1.5 mb-1.5">
                <Icon className={`w-3.5 h-3.5 ${card.color}`} />
                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{card.label}</span>
              </div>
              <p className={`text-lg font-bold ${card.color}`}>{card.value}</p>
            </div>
          );
        })}
      </div>

      {/* Budget Alerts */}
      {budgetAlerts.length > 0 && (
        <div className="bg-white border border-amber-200 rounded-lg p-3" data-testid="budget-alerts">
          <div className="flex items-center gap-1.5 mb-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <span className="text-xs font-semibold text-amber-800">Budget Alerts ({budgetAlerts.length})</span>
          </div>
          <div className="space-y-2">
            {budgetAlerts.map(alert => {
              const isExceeded = alert.alertLevel === 'exceeded';
              return (
                <div key={alert.categoryId} className={`flex items-center justify-between py-1.5 px-2 rounded ${isExceeded ? 'bg-red-50' : 'bg-amber-50'}`} data-testid={`budget-alert-${alert.name}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-2 h-2 rounded-sm" style={{ background: alert.color }} />
                    <span className="text-xs font-medium text-slate-900">{alert.name}</span>
                    <span className={`text-[9px] font-bold px-1 py-0.5 rounded ${isExceeded ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                      {alert.pct}%
                    </span>
                  </div>
                  <div className="text-[10px] text-slate-500 flex-shrink-0 ml-2">
                    ₹{alert.spent.toLocaleString('en-IN')} / ₹{alert.budget.toLocaleString('en-IN')}
                  </div>
                </div>
              );
            })}
          </div>
          <Link to="/categories" className="text-[10px] text-slate-500 hover:text-slate-900 underline mt-2 inline-block" data-testid="view-budgets-link">View all budgets</Link>
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Category Spend */}
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Category Spending (This Month)</h3>
          {stats?.categorySpend?.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={stats.categorySpend} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" tick={chartTick} />
                <YAxis type="category" dataKey="name" tick={chartTick} width={80} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(v) => [`₹${v.toLocaleString('en-IN')}`, 'Amount']}
                />
                <Bar dataKey="total" fill="#0F172A" radius={[0, 4, 4, 0]} barSize={16} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-slate-400 text-center py-10">No spending data yet</p>
          )}
        </div>

        {/* Payment Mode Breakdown */}
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Payment Mode Breakdown</h3>
          {stats?.paymentModeBreakdown?.length > 0 ? (
            <div className="flex items-center gap-4">
              <ResponsiveContainer width="50%" height={180}>
                <PieChart>
                  <Pie
                    data={stats.paymentModeBreakdown}
                    dataKey="total"
                    nameKey="mode"
                    cx="50%"
                    cy="50%"
                    outerRadius={70}
                    innerRadius={40}
                  >
                    {stats.paymentModeBreakdown.map((entry, idx) => (
                      <Cell key={entry.mode} fill={pieColors[idx % pieColors.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => `₹${v.toLocaleString('en-IN')}`} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2">
                {stats.paymentModeBreakdown.map((p, idx) => (
                  <div key={p.mode} className="flex items-center gap-2 text-xs">
                    <div className="w-2.5 h-2.5 rounded-sm" style={{ background: pieColors[idx % pieColors.length] }} />
                    <span className="text-slate-600">{p.mode}</span>
                    <span className="font-medium text-slate-900 ml-auto">₹{p.total.toLocaleString('en-IN')}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-10">No payment data yet</p>
          )}
        </div>
      </div>

      {/* Recent Vouchers */}
      <div className="bg-white border border-slate-200 rounded-lg">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Recent Vouchers</h3>
          <Link to="/vouchers" className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1" data-testid="view-all-vouchers-link">
            View All <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="recent-vouchers-table">
            <thead>
              <tr className="bg-slate-50">
                <th className="text-left px-4 py-2 text-[10px] font-semibold text-slate-500 uppercase">ID</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold text-slate-500 uppercase">Type</th>
                <th className="text-right px-4 py-2 text-[10px] font-semibold text-slate-500 uppercase">Amount</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold text-slate-500 uppercase">Status</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold text-slate-500 uppercase hidden sm:table-cell">Created By</th>
              </tr>
            </thead>
            <tbody>
              {stats?.recentVouchers?.map((v) => (
                <tr key={v._id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2">
                    <Link to={`/vouchers/${v._id}`} className="text-xs font-medium text-slate-900 hover:underline">
                      {v.voucherId}
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-600">{v.voucherType}</td>
                  <td className="px-4 py-2 text-xs font-medium text-slate-900 text-right">₹{v.amount?.toLocaleString('en-IN')}</td>
                  <td className="px-4 py-2"><StatusBadge status={v.status} /></td>
                  <td className="px-4 py-2 text-xs text-slate-500 hidden sm:table-cell">{v.createdBy?.name || '-'}</td>
                </tr>
              ))}
              {(!stats?.recentVouchers || stats.recentVouchers.length === 0) && (
                <tr><td colSpan="5" className="text-center py-8 text-sm text-slate-400">No vouchers yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    Draft: 'bg-slate-100 text-slate-600 border-slate-200',
    Pending: 'bg-amber-50 text-amber-700 border-amber-200',
    Approved: 'bg-green-50 text-green-700 border-green-200',
    Rejected: 'bg-red-50 text-red-700 border-red-200',
    Settled: 'bg-blue-50 text-blue-700 border-blue-200',
    Cancelled: 'bg-slate-100 text-slate-500 border-slate-200',
  };
  return (
    <span className={`inline-block text-[10px] font-medium px-2 py-0.5 rounded-full border ${styles[status] || styles.Draft}`} data-testid={`status-badge-${status?.toLowerCase()}`}>
      {status}
    </span>
  );
}

export { StatusBadge };
